import { FormattedMessage } from 'react-intl'
import { number } from 'prop-types'
import { Root, Title } from './recently-viewed.styled'
import { Slide } from '../../particles/slider'
import TileConnected from '../../molecules/tile/connected/tile-connected'
import { useConfig } from '@bluheadless/ui-logic/src/providers/config'
import { slidesPerViewForBreakpoints } from '@bluheadless/ui-logic/src/utils/commons'
import { useRecentlyViewedProducts } from '@bluheadless/ui-logic/src/hooks/product/useRecentlyViewedProducts'
import { useTheme, useThemeProps } from '@mui/system'
import Carousel from '../../organisms/carousel'
import { cx } from '@emotion/css'
import SkeletonCard from '../../molecules/skeleton/card/skeleton-card'
import round from 'lodash/round'
import { usePageContext } from '@bluheadless/ui-logic/src/providers/page/page'

const WIDGET_CONTEXT = 'recently-viewed'

const RecentlyViewed = ({ productId, className, ...inProps }) => {
	const props = useThemeProps({
		name: 'BHRecentlyViewed',
		props: inProps,
	})

	const { skeletonProps, sliderProps, showTitle, tileProps } = props

	const theme = useTheme()

	const {
		catalog: {
			products: {
				images: { width: productImageWidth, height: productImageHeight },
				recentlyViewed: {
					slider: { slidesPerView: _slidesPerView },
				},
			},
		},
	} = useConfig()
	const cardRatio = productImageWidth / productImageHeight

	const { entityType: pageType, __bhEntityId: pageId } = usePageContext()

	const {
		recentlyViewedProductIds,
		recentlyViewedProducts,
		loading: loadingRecentlyViewed,
	} = useRecentlyViewedProducts({ id: productId, pageContext: pageType, pageId, widgetContext: WIDGET_CONTEXT })

	const slidesPerView = slidesPerViewForBreakpoints(_slidesPerView, theme.breakpoints)
	const responsiveProps = Object.entries(_slidesPerView)
		.map(([breakpoint, value]) => [
			breakpoint,
			{
				columns: round(parseFloat(value)),
				columnGap: skeletonProps.columnGap + 'px',
			},
		])
		.reduce((acc, [breakpoint, value]) => {
			return { ...acc, [breakpoint]: value }
		}, {})

	const breakpointsMerged = Object.entries(sliderProps.breakpoints)
		.map(([breakpoint, value]) => [
			theme.breakpoints.values[breakpoint],
			{
				...value,
				slidesPerView: _slidesPerView[breakpoint],
			},
		])
		.reduce((acc, [breakpoint, value]) => {
			return { ...acc, [breakpoint]: value }
		}, {})

	return recentlyViewedProducts?.length > 0 ? (
		<Root className={cx('RecentlyViewed-root', className)} {...props}>
			{recentlyViewedProductIds.length > 0 && loadingRecentlyViewed ? (
				<SkeletonCard cardRatio={cardRatio} responsiveProps={responsiveProps} />
			) : (
				<Carousel
					{...sliderProps}
					breakpoints={breakpointsMerged}
					titleComponent={
						showTitle ? (
							<Title component="div" variant="h3">
								<FormattedMessage id="recently_viewed_products_title" />
							</Title>
						) : null
					}
					slidesPerView={slidesPerView.xs}
				>
					{recentlyViewedProducts.map((product, index) => (
						<Slide key={index}>
							<TileConnected
								position={index}
								product={product}
								wishListProps={{ position: 'outer', variant: 'icon' }}
								title1Props={{ component: 'h4' }}
								widgetContext={WIDGET_CONTEXT}
								{...tileProps}
							/>
						</Slide>
					))}
				</Carousel>
			)}
		</Root>
	) : null
}

RecentlyViewed.propTypes = {
	productId: number,
}

export default RecentlyViewed
